import axios from '../axios'
import type { ConversationSession, Message } from '../utils/conversationStorage.ts'

const authHeaders = () => {
  const token = localStorage.getItem('access-Token-Edify')
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} }
}

const unwrapList = (res: unknown): unknown[] => {
  if (!res || typeof res !== 'object') return []
  const r = res as Record<string, unknown>
  const d = r.data
  if (Array.isArray(d)) return d
  if (d && typeof d === 'object') {
    const inner = d as Record<string, unknown>
    if (Array.isArray(inner.conversations)) return inner.conversations
    if (Array.isArray(inner.items)) return inner.items
    if (Array.isArray(inner.data)) return inner.data as unknown[]
  }
  if (Array.isArray(r.conversations)) return r.conversations
  return []
}

const unwrapMessages = (res: unknown): unknown[] => {
  if (!res || typeof res !== 'object') return []
  const r = res as Record<string, unknown>
  const d = r.data
  if (Array.isArray(d)) return d
  if (d && typeof d === 'object') {
    const inner = d as Record<string, unknown>
    if (Array.isArray(inner.messages)) return inner.messages
    if (Array.isArray(inner.items)) return inner.items
  }
  if (Array.isArray(r.messages)) return r.messages
  return []
}

export function normalizeApiConversationId(raw: unknown): string | undefined {
  if (typeof raw === 'number' && Number.isFinite(raw)) return String(Math.trunc(raw))
  if (typeof raw === 'string' && /^\d+$/.test(raw.trim())) return raw.trim()
  return undefined
}

export type ApiChatConversationRow = {
  id?: string | number
  title?: string | null
  clientSessionId?: string | null
  client_session_id?: string | null
  createdAt?: string
  created_at?: string
  updatedAt?: string
  updated_at?: string
  lastUpdated?: string
  last_updated?: string
  messageCount?: number
  message_count?: number
}

export type ApiChatMessageRow = {
  id?: number | string
  role?: string
  content?: string
  createdAt?: string
  created_at?: string
  timestamp?: string
  actionId?: string | null
  meta?: Record<string, unknown> | null
}

export function mapApiConversationRow(row: ApiChatConversationRow): ConversationSession | null {
  const idStr = normalizeApiConversationId(row?.id)
  if (!idStr) return null
  const clientId = row.clientSessionId ?? row.client_session_id ?? undefined
  const sessionId =
    typeof clientId === 'string' && clientId.length > 0 ? clientId : `conv-${idStr}`
  const lastUpdated =
    row.lastUpdated ??
    row.last_updated ??
    row.updatedAt ??
    row.updated_at ??
    row.createdAt ??
    row.created_at ??
    new Date().toISOString()
  const createdAt = row.createdAt ?? row.created_at ?? lastUpdated
  const messageCount = row.messageCount ?? row.message_count ?? 0
  const rawTitle = row.title
  const title =
    typeof rawTitle === 'string' && rawTitle.trim().length > 0 ? rawTitle.trim() : undefined
  return {
    sessionId,
    messages: [],
    createdAt,
    lastUpdated,
    messageCount,
    isActive: false,
    conversationId: idStr,
    title: title?.trim() ? title : undefined
  }
}

function inferCreatedPayloadFromAssistantMeta(meta: Record<string, unknown>): {
  createdPortfolio?: Message['createdPortfolio']
  createdPost?: Message['createdPost']
} {
  if (meta.needsData === true) return {}
  if (meta.createdPortfolio && typeof meta.createdPortfolio === 'object') return {}
  if (meta.createdPost && typeof meta.createdPost === 'object') return {}
  const raw = meta.data
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const data = raw as Record<string, unknown>
  if (data.id === undefined || data.id === null) return {}
  const typ = data.type
  if (typ === 'producto' || typ === 'servicio') {
    if (Array.isArray(data.portfolio_images)) {
      return {
        createdPortfolio: {
          ...(data as unknown as Message['createdPortfolio']),
          type: typ === 'servicio' ? 'servicio' : 'producto'
        }
      }
    }
  }
  if (
    typ !== 'producto' &&
    typ !== 'servicio' &&
    (typeof data.content === 'string' ||
      Array.isArray(data.images) ||
      Array.isArray(data.video))
  ) {
    return { createdPost: data as Message['createdPost'] }
  }
  return {}
}

export function serverMessageRowToMessage(row: ApiChatMessageRow): Message | null {
  if (!row || typeof row !== 'object') return null
  const role = row.role === 'user' || row.role === 'assistant' ? row.role : null
  if (!role || typeof row.content !== 'string') return null
  const ts = row.createdAt ?? row.created_at ?? row.timestamp ?? new Date().toISOString()
  const id =
    row.id !== undefined && row.id !== null
      ? String(row.id)
      : `local-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
  const meta = row.meta && typeof row.meta === 'object' ? (row.meta as Record<string, unknown>) : undefined
  const inferred =
    role === 'assistant' && meta ? inferCreatedPayloadFromAssistantMeta(meta) : {}
  const llmRaw =
    meta && typeof meta.llmRawResponse === 'string'
      ? meta.llmRawResponse
      : meta && typeof (meta as { llm_raw_response?: string }).llm_raw_response === 'string'
        ? (meta as { llm_raw_response: string }).llm_raw_response
        : undefined

  const actionPart: Pick<Message, 'actionId'> | Record<string, never> =
    row.actionId === null
      ? { actionId: null }
      : row.actionId !== undefined && String(row.actionId).length > 0
        ? { actionId: String(row.actionId) }
        : meta && meta.actionId === null
          ? { actionId: null }
          : meta && meta.actionId !== undefined && meta.actionId !== null
            ? { actionId: String(meta.actionId) }
            : {}

  const createdPortfolioFromMeta =
    meta?.createdPortfolio && typeof meta.createdPortfolio === 'object'
      ? (meta.createdPortfolio as Message['createdPortfolio'])
      : undefined
  const createdPostFromMeta =
    meta?.createdPost && typeof meta.createdPost === 'object'
      ? (meta.createdPost as Message['createdPost'])
      : undefined
  const createdPortfolio = createdPortfolioFromMeta ?? inferred.createdPortfolio
  const createdPost = createdPostFromMeta ?? inferred.createdPost

  const base: Message = {
    id,
    role,
    content: row.content,
    timestamp: ts,
    ...(llmRaw ? { llmRawResponse: llmRaw } : {}),
    ...actionPart,
    ...(meta?.guideMode === true ? { guideMode: true } : {}),
    ...(meta?.needsData === true && meta.data != null
      ? {
          needsData: true,
          data: meta.data,
          dataTotal: typeof meta.total === 'number' ? meta.total : 0
        }
      : {}),
    ...(typeof meta?.quotationType === 'string' ? { quotationType: meta.quotationType } : {}),
    ...(typeof meta?.workVisibility === 'string' ? { workVisibility: meta.workVisibility } : {}),
    ...(typeof meta?.isPublic === 'boolean' ? { isPublic: meta.isPublic } : {}),
    ...(typeof meta?.noResultsMessage === 'string' ? { noResultsMessage: meta.noResultsMessage } : {}),
    ...(createdPortfolio ? { createdPortfolio } : {}),
    ...(createdPost ? { createdPost } : {})
  }
  return base
}

export function mapApiMessageRow(row: ApiChatMessageRow): Message | null {
  return serverMessageRowToMessage(row)
}

export async function fetchChatConversationsFromApi(): Promise<ConversationSession[]> {
  const response = await axios.get<unknown>('/user/chat/conversations', authHeaders())
  const rows = unwrapList(response.data) as ApiChatConversationRow[]
  const out: ConversationSession[] = []
  for (const r of rows) {
    const m = mapApiConversationRow(r)
    if (m) out.push(m)
  }
  return out
}

export async function fetchConversationMessagesFromApi(conversationId: string): Promise<Message[]> {
  const cid = conversationId.trim()
  if (!/^\d+$/.test(cid)) return []
  const response = await axios.get<unknown>(
    `/user/chat/conversations/${cid}/messages`,
    authHeaders()
  )
  const rows = unwrapMessages(response.data) as ApiChatMessageRow[]
  const out: Message[] = []
  for (const row of rows) {
    const m = mapApiMessageRow(row)
    if (m) out.push(m)
  }
  return out
}

export function mergeServerConversationsWithLocal(
  server: ConversationSession[],
  local: ConversationSession[]
): ConversationSession[] {
  const consumed = new Set<string>()
  const out: ConversationSession[] = []

  for (const srv of server) {
    if (srv.conversationId == null) continue
    const match = local.find(
      (l) =>
        (l.conversationId != null && l.conversationId === srv.conversationId) ||
        l.sessionId === srv.sessionId
    )
    if (match) {
      consumed.add(match.sessionId)
      out.push({
        ...match,
        conversationId: srv.conversationId,
        title: srv.title ?? match.title,
        messageCount: srv.messageCount ?? match.messageCount,
        lastUpdated:
          new Date(srv.lastUpdated).getTime() >= new Date(match.lastUpdated).getTime()
            ? srv.lastUpdated
            : match.lastUpdated,
        messages: match.messages.length > 0 ? match.messages : srv.messages,
        createdAt: match.createdAt,
        isActive: match.isActive
      })
    } else {
      out.push({ ...srv })
    }
  }

  for (const loc of local) {
    if (!consumed.has(loc.sessionId)) {
      out.push(loc)
    }
  }

  out.sort((a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime())
  return out
}
